import { type QueryClient, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  type LinkProps,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'
import { z } from 'zod'
import { api, NonConnecte, requeteMoi, requetes } from './api'
import { BandeauAccesTemporaires } from './composants/AccesTemporaires'
import { PanneauAssistant } from './composants/AssistantChat'
import { HoteConfirmation } from './composants/HoteConfirmation'
import { Icone, type NomIcone } from './composants/Icone'
import { initiales } from './libelles'
import { Abonnements } from './pages/Abonnements'
import { AccesTemporaires } from './pages/AccesTemporaires'
import { Accueil } from './pages/Accueil'
import { BoiteEnvoi, BrouillonFiche } from './pages/BoiteEnvoi'
import { Circuits } from './pages/Circuits'
import { Collecteurs } from './pages/Collecteurs'
import { Installer } from './pages/Installer'
import { Introuvable } from './pages/Introuvable'
import { Justificatifs } from './pages/Justificatifs'
import { JustificatifsConnexions } from './pages/JustificatifsConnexions'
import { MesEnvois } from './pages/MesEnvois'
import { Photo } from './pages/Photo'
import { BarriereVersion } from './pwa/BarriereVersion'
import { PiloteFileEnvoi } from './pwa/PiloteFileEnvoi'
import { PastilleAppli } from './composants/PastilleAppli'
import { SuiviRangement } from './composants/SuiviRangement'
import { Connexion } from './pages/Connexion'
import { EcranDefi, SurimpressionDefis } from './pages/Defi'
import { EcheanceFiche } from './pages/EcheanceFiche'
import { Echeancier } from './pages/Echeancier'
import { Paiements } from './pages/Paiements'
import { Rapprochement } from './pages/Rapprochement'
import { Recherche } from './pages/Recherche'
import { Refacturations } from './pages/Refacturations'
import { ONGLETS_CONNEXIONS, ONGLETS_REGLAGES, Reglages } from './pages/Reglages'
import { PieceFiche } from './pages/PieceFiche'
import { PlanClassement } from './pages/PlanClassement'
import { Pieces } from './pages/Pieces'
import { Autoriser } from './pages/Autoriser'
import { Releves } from './pages/Releves'
import { Sante, SoinFiche } from './pages/Sante'
import { TacheFiche } from './pages/TacheFiche'
import { ONGLETS_PROJET, ProjetFiche } from './pages/ProjetFiche'
import { Projets } from './pages/Projets'
import { Taches } from './pages/Taches'
import { EntiteFiche } from './pages/EntiteFiche'
import { Entites } from './pages/Entites'
import { Personnes } from './pages/Personnes'
import { Tiers } from './pages/Tiers'
import { TiersFiche } from './pages/TiersFiche'
import { BasculeTheme } from './theme'

type EntreeNavigation = {
  to: NonNullable<LinkProps['to']>
  icone: NomIcone
  libelle: string
  exact?: boolean
  /** Réservé aux administrateurs (le foyer). */
  administrateurs?: boolean
  /**
   * Ouverte en plus à une titulaire d'espace privé, filtrée à son espace :
   * administrateurs ou qui a au moins un espace privé.
   */
  espacePrive?: boolean
  /**
   * Accès temporaires : seulement pour qui en a l'usage (titulaire d'un espace privé, ou
   * administrateur quand un espace privé existe) ; sans entité privée, l'entrée n'est pas là (I6).
   */
  accesTemporaires?: boolean
  /** Autres pages qui allument l'entrée (pages réunies par des onglets). */
  aussi?: string[]
}

// Le menu en quatre groupes : le quotidien (sans titre), le courrier qui entre et sort, la
// comptabilité, l'organisation. Un groupe dont aucune entrée n'est visible disparaît.
const NAVIGATION: { titre?: string; entrees: EntreeNavigation[] }[] = [
  {
    entrees: [
      { to: '/', icone: 'home', libelle: 'Accueil', exact: true },
      { to: '/recherche', icone: 'search', libelle: 'Recherche' },
      { to: '/taches', icone: 'task_alt', libelle: 'Tâches' },
      { to: '/projets', icone: 'rocket_launch', libelle: 'Projets' },
      { to: '/echeancier', icone: 'calendar_month', libelle: 'Échéancier' },
    ],
  },
  {
    titre: 'Courrier',
    entrees: [
      { to: '/photo', icone: 'photo_camera', libelle: 'Photo' },
      { to: '/envois', icone: 'outbox', libelle: 'Mes envois' },
      { to: '/boite-envoi', icone: 'forward_to_inbox', libelle: 'Boîte d’envoi' },
    ],
  },
  {
    titre: 'Comptabilité',
    entrees: [
      { to: '/pieces', icone: 'description', libelle: 'Pièces' },
      { to: '/collecteurs', icone: 'download', libelle: 'Collecteurs' },
      { to: '/justificatifs', icone: 'fact_check', libelle: 'Justificatifs' },
      { to: '/releves', icone: 'account_balance', libelle: 'Relevés' },
      { to: '/paiements', icone: 'payments', libelle: 'Paiements' },
      { to: '/refacturations', icone: 'receipt_long', libelle: 'À refacturer' },
      { to: '/abonnements', icone: 'event_repeat', libelle: 'Abonnements' },
      { to: '/sante', icone: 'health_and_safety', libelle: 'Santé', administrateurs: true },
    ],
  },
  {
    titre: 'Organisation',
    entrees: [
      {
        to: '/entites',
        icone: 'contacts',
        libelle: 'Référentiel',
        aussi: ['/tiers', '/personnes'],
      },
      { to: '/circuits', icone: 'account_tree', libelle: 'Circuits', espacePrive: true },
      {
        to: '/acces-temporaires',
        icone: 'key',
        libelle: 'Accès temporaires',
        accesTemporaires: true,
      },
      { to: '/reglages', icone: 'settings', libelle: 'Réglages' },
    ],
  },
]

function Laterale() {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const navigate = useNavigate()
  const chemin = useRouterState({ select: (e) => e.location.pathname })
  const sous = (racine: string) => chemin === racine || chemin.startsWith(`${racine}/`)
  const semaine = useQuery(requetes.taches({ vue: 'ma_semaine' }))
  const decisions = useQuery(requetes.decisions)
  const aEnvoyer = useQuery(requetes.brouillons({ vue: 'a_traiter' }))
  // Tâches nouvelles : celles rangées dans un projet allument « Projets », les autres « Tâches ».
  const lesNouvelles = useQuery(requetes.nouvellesTaches).data
  const nouvellesProjets = lesNouvelles?.projets.reduce((n, p) => n + p.nombre, 0) ?? 0
  const pastilles: Record<string, number> = {
    '/taches': (lesNouvelles?.nombre ?? 0) - nouvellesProjets,
    '/projets': nouvellesProjets,
  }
  // Opérations du cabinet qui attendent leur justificatif, toutes connexions visibles confondues.
  const portails = useQuery({ ...requetes.portails, staleTime: 60_000 })
  // Collecteurs à lancer (raison et bouton), toutes connexions visibles confondues.
  const aLancer = useQuery({ ...requetes.collecteurs, staleTime: 60_000 })
  const compteurs: Record<string, number | undefined> = {
    '/taches': semaine.data?.filter((t) => t.type !== 'decision').length,
    '/releves': decisions.data?.length,
    '/boite-envoi': aEnvoyer.data?.length,
    '/collecteurs': aLancer.data?.filter((c) => c.raisons.length > 0 && c.lancable).length,
    '/justificatifs': portails.data?.connexions.reduce(
      (n, c) => n + (c.justificatifs?.aJustifier ?? 0),
      0,
    ),
  }

  // ⌘K ou Ctrl+K : la recherche, de n'importe quelle page.
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        void navigate({ to: '/recherche' })
      }
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  }, [navigate])

  async function deconnexion() {
    await api.auth.deconnexion.$post()
    client.clear()
    await navigate({ to: '/connexion' })
  }

  return (
    <aside className="laterale">
      <Link to="/" className="marque">
        <span className="marque-logo">
          <Icone nom="eco" plein taille={19} />
        </span>
        <span className="marque-texte">
          <strong>admin PMVB</strong>
          <span>Assistant administratif</span>
        </span>
      </Link>
      <nav className="navigation" aria-label="Navigation principale">
        {NAVIGATION.map((groupe) => ({
          ...groupe,
          entrees: groupe.entrees.filter(
            (n) =>
              (!n.administrateurs || moi.administrateur) &&
              (!n.espacePrive || moi.administrateur || moi.espacesPrives.length > 0) &&
              (!n.accesTemporaires || moi.voitAccesTemporaires),
          ),
        }))
          .filter((groupe) => groupe.entrees.length > 0)
          .map((groupe) => (
            <div
              key={groupe.titre ?? ''}
              className="navigation-groupe"
              role="group"
              aria-label={groupe.titre}
            >
              {groupe.titre && <span className="navigation-titre">{groupe.titre}</span>}
              {groupe.entrees.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  activeOptions={{ exact: n.exact ?? false }}
                  className={n.aussi?.some(sous) ? 'actif' : undefined}
                  aria-current={n.aussi?.some(sous) ? 'page' : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <Icone nom={n.icone} plein={isActive || !!n.aussi?.some(sous)} />
                      <span className="libelle">{n.libelle}</span>
                      {!!pastilles[n.to] && (
                        <span
                          className="pastille-nouvelles"
                          title={`${pastilles[n.to]} tâche${pastilles[n.to]! > 1 ? 's' : ''} nouvelle${pastilles[n.to]! > 1 ? 's' : ''}`}
                        >
                          {pastilles[n.to]}
                        </span>
                      )}
                      {!!compteurs[n.to] && <span className="compteur">{compteurs[n.to]}</span>}
                    </>
                  )}
                </Link>
              ))}
            </div>
          ))}
      </nav>
      <div className="laterale-pied">
        <BasculeTheme />
        <div className="utilisateur">
          <span className="avatar">{initiales(moi.nom).slice(0, 3)}</span>
          <span className="utilisateur-nom">
            <span>{moi.nom.split(' ')[0]}</span>
            <span>{moi.administrateur ? 'Administrateur' : 'Associé'}</span>
          </span>
          <button
            type="button"
            className="bouton-icone"
            onClick={deconnexion}
            title="Se déconnecter"
            aria-label="Se déconnecter"
          >
            <Icone nom="logout" taille={19} />
          </button>
        </div>
      </div>
    </aside>
  )
}

const racine = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <BarriereVersion />
      <Outlet />
      <HoteConfirmation />
    </>
  ),
  notFoundComponent: Introuvable,
})

// Espace connecté : toutes les pages sauf la connexion exigent une session.
const connecte = createRoute({
  getParentRoute: () => racine,
  id: 'connecte',
  // La file d'envoi du téléphone se vide dès qu'une page connectée est ouverte.
  component: () => (
    <div className="coquille">
      <PiloteFileEnvoi />
      <Laterale />
      <Outlet />
      <PanneauAssistant />
      <SuiviRangement />
      <SurimpressionDefis />
      <BandeauAccesTemporaires />
      <PastilleAppli />
    </div>
  ),
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(requeteMoi)
    } catch (e) {
      // Après la connexion, retour à la page demandée (consentement OAuth, lien profond).
      if (e instanceof NonConnecte)
        throw redirect({
          to: '/connexion',
          search: location.href === '/' ? {} : { retour: location.href },
        })
      throw e
    }
  },
})

// Onglets des listes, lus dans l'adresse : un lien partagé rouvre la même vue sur tout appareil.
const VUES_TACHES = ['ma_semaine', 'tableau', 'en_attente_tiers', 'reportees', 'toutes'] as const
/** Onglets de la page Tâches : « toutes » ne sert qu'au parcours d'une fiche. */
const ONGLETS_TACHES = ['ma_semaine', 'tableau', 'en_attente_tiers', 'reportees'] as const
const VUES_PIECES = ['recentes', 'non_rattachees', 'a_suivre', 'toutes'] as const
const VUES_PAIEMENTS = [
  'a_payer',
  'a_encaisser',
  'en_retard',
  'partiels',
  'a_rapprocher',
  'releves_manquants',
] as const
const VUES_BROUILLONS = ['a_traiter', 'faits', 'tous'] as const
const VUES_ECHEANCIER = ['frise', 'semaine', 'agenda', 'liste'] as const

const accueil = createRoute({ getParentRoute: () => connecte, path: '/', component: Accueil })
const photo = createRoute({ getParentRoute: () => connecte, path: '/photo', component: Photo })
const envois = createRoute({
  getParentRoute: () => connecte,
  path: '/envois',
  component: MesEnvois,
})
const installer = createRoute({
  getParentRoute: () => connecte,
  path: '/installer',
  component: Installer,
})
const entites = createRoute({
  getParentRoute: () => connecte,
  path: '/entites',
  component: Entites,
})
const entite = createRoute({
  getParentRoute: () => connecte,
  path: '/entites/$id',
  component: function PageEntite() {
    const { id } = entite.useParams()
    return <EntiteFiche key={id} id={id} />
  },
})
const planEntite = createRoute({
  getParentRoute: () => connecte,
  path: '/entites/$id/plan',
  component: function PagePlan() {
    const { id } = planEntite.useParams()
    return <PlanClassement key={id} id={id} />
  },
})
const echeancier = createRoute({
  getParentRoute: () => connecte,
  path: '/echeancier',
  validateSearch: z.object({ vue: z.enum(VUES_ECHEANCIER).optional().catch(undefined) }),
  component: function PageEcheancier() {
    const { vue } = echeancier.useSearch()
    const naviguer = echeancier.useNavigate()
    return (
      <Echeancier
        vue={vue ?? 'frise'}
        onChangeVue={(v) =>
          naviguer({ search: { vue: v === 'frise' ? undefined : v }, replace: true })
        }
      />
    )
  },
})
const echeance = createRoute({
  getParentRoute: () => connecte,
  path: '/echeances/$id',
  component: function PageEcheance() {
    const { id } = echeance.useParams()
    return <EcheanceFiche key={id} id={id} />
  },
})
const taches = createRoute({
  getParentRoute: () => connecte,
  path: '/taches',
  // L'onglet et « pour qui » dans l'adresse : un lien rouvre la même liste, le retour d'une
  // fiche aussi.
  validateSearch: z.object({
    vue: z.enum(ONGLETS_TACHES).optional().catch(undefined),
    pour: z.string().max(64).optional().catch(undefined),
  }),
  component: function PageTaches() {
    const { vue, pour } = taches.useSearch()
    const naviguer = taches.useNavigate()
    return (
      <Taches
        vue={vue ?? 'ma_semaine'}
        pour={pour}
        onChangeVue={(v) =>
          naviguer({
            search: (s) => ({ ...s, vue: v === 'ma_semaine' || v === 'toutes' ? undefined : v }),
            replace: true,
          })
        }
        onChangePour={(p) => naviguer({ search: (s) => ({ ...s, pour: p }), replace: true })}
      />
    )
  },
})
const tache = createRoute({
  getParentRoute: () => connecte,
  path: '/taches/$id',
  // vue : la liste parcourue une à une (précédente, suivante) ; « Ma semaine » par défaut.
  validateSearch: z.object({ vue: z.enum(VUES_TACHES).optional().catch(undefined) }),
  component: function PageTache() {
    const { id } = tache.useParams()
    const { vue } = tache.useSearch()
    return <TacheFiche key={id} id={id} vue={vue ?? 'ma_semaine'} />
  },
})
const projets = createRoute({
  getParentRoute: () => connecte,
  path: '/projets',
  component: Projets,
})
const projet = createRoute({
  getParentRoute: () => connecte,
  path: '/projets/$id',
  // L'onglet et la page ouverts dans l'adresse (absents : Pages, l'objectif) : un lien rouvre
  // le même endroit.
  validateSearch: z.object({
    onglet: z.enum(ONGLETS_PROJET).optional().catch(undefined),
    page: z.uuid().optional().catch(undefined),
  }),
  component: function PageProjet() {
    const { id } = projet.useParams()
    const { onglet, page } = projet.useSearch()
    const naviguer = projet.useNavigate()
    return (
      <ProjetFiche
        key={id}
        id={id}
        pageId={page}
        onglet={onglet ?? 'pages'}
        onNaviguer={(vers) =>
          naviguer({
            search: {
              ...(vers.onglet && vers.onglet !== 'pages' && { onglet: vers.onglet }),
              ...(vers.page && { page: vers.page }),
            },
          })
        }
      />
    )
  },
})
const paiements = createRoute({
  getParentRoute: () => connecte,
  path: '/paiements',
  validateSearch: z.object({ vue: z.enum(VUES_PAIEMENTS).optional().catch(undefined) }),
  component: function PagePaiements() {
    const { vue } = paiements.useSearch()
    const naviguer = paiements.useNavigate()
    return (
      <Paiements
        vue={vue ?? 'a_payer'}
        onChangeVue={(v) =>
          naviguer({ search: { vue: v === 'a_payer' ? undefined : v }, replace: true })
        }
      />
    )
  },
})
const refacturations = createRoute({
  getParentRoute: () => connecte,
  path: '/refacturations',
  validateSearch: z.object({ entiteId: z.uuid().optional().catch(undefined) }),
  component: function PageRefacturations() {
    const { entiteId } = refacturations.useSearch()
    return <Refacturations entiteId={entiteId} />
  },
})
const abonnements = createRoute({
  getParentRoute: () => connecte,
  path: '/abonnements',
  validateSearch: z.object({ entiteId: z.uuid().optional().catch(undefined) }),
  component: function PageAbonnements() {
    const { entiteId } = abonnements.useSearch()
    return <Abonnements entiteId={entiteId} />
  },
})
const pieces = createRoute({
  getParentRoute: () => connecte,
  path: '/pieces',
  // q : la recherche de « Toutes », gardée dans l'adresse comme l'onglet.
  validateSearch: z.object({
    vue: z.enum(VUES_PIECES).optional().catch(undefined),
    q: z.string().max(200).optional().catch(undefined),
  }),
  component: function PagePieces() {
    const { vue, q } = pieces.useSearch()
    const naviguer = pieces.useNavigate()
    return (
      <Pieces
        vue={vue ?? 'recentes'}
        recherche={q ?? ''}
        onChangeVue={(v) =>
          naviguer({ search: { vue: v === 'recentes' ? undefined : v }, replace: true })
        }
        onRechercher={(texte) =>
          naviguer({ search: { vue: 'toutes', q: texte || undefined }, replace: true })
        }
      />
    )
  },
})
const piece = createRoute({
  getParentRoute: () => connecte,
  path: '/pieces/$id',
  // vue : la liste parcourue une à une (précédente, suivante) ; aucune hors de la page Pièces.
  validateSearch: z.object({ vue: z.enum(VUES_PIECES).optional().catch(undefined) }),
  component: function PagePiece() {
    const { id } = piece.useParams()
    const { vue } = piece.useSearch()
    return <PieceFiche key={id} id={id} vue={vue} />
  },
})
const recherche = createRoute({
  getParentRoute: () => connecte,
  path: '/recherche',
  // q dans l'adresse : un lien rouvre la même recherche, le retour d'une fiche aussi.
  validateSearch: z.object({ q: z.string().max(100).optional().catch(undefined) }),
  component: function PageRecherche() {
    const { q } = recherche.useSearch()
    const naviguer = recherche.useNavigate()
    const onRechercher = useCallback(
      (texte: string) => naviguer({ search: { q: texte || undefined }, replace: true }),
      [naviguer],
    )
    return <Recherche recherche={q ?? ''} onRechercher={onRechercher} />
  },
})
const releves = createRoute({
  getParentRoute: () => connecte,
  path: '/releves',
  component: Releves,
})
const rapprochement = createRoute({
  getParentRoute: () => connecte,
  path: '/rapprochements/$id',
  component: function PageRapprochement() {
    const { id } = rapprochement.useParams()
    return <Rapprochement key={id} id={id} />
  },
})
// Collecteurs à la demande (décision du 21/10/2026) : ceux à lancer d'abord, avec leurs raisons.
const collecteurs = createRoute({
  getParentRoute: () => connecte,
  path: '/collecteurs',
  component: Collecteurs,
})
// Bilan des justificatifs d'une connexion de portail du cabinet (Tiime) : passage à blanc,
// listes du plan, décisions d'un humain ; rien n'y est écrit chez le portail.
const listeJustificatifs = createRoute({
  getParentRoute: () => connecte,
  path: '/justificatifs',
  component: JustificatifsConnexions,
})
const justificatifs = createRoute({
  getParentRoute: () => connecte,
  path: '/justificatifs/$id',
  component: function PageJustificatifs() {
    const { id } = justificatifs.useParams()
    return <Justificatifs key={id} id={id} />
  },
})
const autoriser = createRoute({
  getParentRoute: () => connecte,
  path: '/autoriser',
  validateSearch: z.object({
    client_id: z.string().optional(),
    redirect_uri: z.string().optional(),
    code_challenge: z.string().optional(),
    code_challenge_method: z.string().optional(),
    response_type: z.string().optional(),
    state: z.string().optional(),
    resource: z.string().optional(),
    scope: z.string().optional(),
  }),
  component: function PageAutoriser() {
    return <Autoriser demande={autoriser.useSearch()} />
  },
})
const reglages = createRoute({
  getParentRoute: () => connecte,
  path: '/reglages',
  // Retour de la connexion d'une boîte Gmail (/api/boites/google) ou d'une banque (/api/banques).
  validateSearch: z.object({
    boite: z.string().optional(),
    adresse: z.string().optional(),
    banque: z.string().optional(),
    onglet: z.enum(ONGLETS_REGLAGES).optional().catch(undefined),
    connexion: z.enum(ONGLETS_CONNEXIONS).optional().catch(undefined),
  }),
  component: function PageReglages() {
    const recherche = reglages.useSearch()
    const naviguer = reglages.useNavigate()
    return (
      <Reglages
        resultatBoite={recherche.boite}
        adresseBoite={recherche.adresse}
        resultatBanque={recherche.banque}
        onglet={recherche.onglet}
        onChangeOnglet={(onglet) => naviguer({ search: { onglet }, replace: true })}
        connexion={recherche.connexion}
        onChangeConnexion={(connexion) =>
          naviguer({ search: { onglet: 'connexions', connexion }, replace: true })
        }
      />
    )
  },
})
const boiteEnvoi = createRoute({
  getParentRoute: () => connecte,
  path: '/boite-envoi',
  validateSearch: z.object({ vue: z.enum(VUES_BROUILLONS).optional().catch(undefined) }),
  component: function PageBoiteEnvoi() {
    const { vue } = boiteEnvoi.useSearch()
    const naviguer = boiteEnvoi.useNavigate()
    return (
      <BoiteEnvoi
        vue={vue ?? 'a_traiter'}
        onChangeVue={(v) =>
          naviguer({ search: { vue: v === 'a_traiter' ? undefined : v }, replace: true })
        }
      />
    )
  },
})
const brouillon = createRoute({
  getParentRoute: () => connecte,
  path: '/boite-envoi/$id',
  component: function PageBrouillon() {
    const { id } = brouillon.useParams()
    return <BrouillonFiche key={id} id={id} />
  },
})
const sante = createRoute({ getParentRoute: () => connecte, path: '/sante', component: Sante })
// Audit quotidien (administrateurs) : sources, circuit des pièces, automatismes.
const circuits = createRoute({
  getParentRoute: () => connecte,
  path: '/circuits',
  component: Circuits,
})
const soin = createRoute({
  getParentRoute: () => connecte,
  path: '/sante/soins/$id',
  component: function PageSoin() {
    const { id } = soin.useParams()
    return <SoinFiche key={id} id={id} />
  },
})
// Écran d'un défi : ouvert par la notification, plein écran, par-dessus la coquille.
const defi = createRoute({
  getParentRoute: () => connecte,
  path: '/defi/$id',
  component: function PageDefi() {
    const { id } = defi.useParams()
    const naviguer = useNavigate()
    const router = useRouter()
    return (
      <EcranDefi
        key={id}
        id={id}
        // Retour d'où l'on venait ; ouvert depuis la notification (pas d'historique) : l'accueil.
        onFermer={() =>
          router.history.canGoBack() ? router.history.back() : void naviguer({ to: '/' })
        }
        onSuivant={(suivant) =>
          void naviguer({ to: '/defi/$id', params: { id: suivant }, replace: true })
        }
      />
    )
  },
})
const tiers = createRoute({ getParentRoute: () => connecte, path: '/tiers', component: Tiers })
const tiersFiche = createRoute({
  getParentRoute: () => connecte,
  path: '/tiers/$id',
  component: function PageTiers() {
    const { id } = tiersFiche.useParams()
    return <TiersFiche key={id} id={id} />
  },
})
// Accès temporaires à un espace privé : décisions de la titulaire, demandes d'un administrateur.
const accesTemporaires = createRoute({
  getParentRoute: () => connecte,
  path: '/acces-temporaires',
  component: AccesTemporaires,
})
const personnes = createRoute({
  getParentRoute: () => connecte,
  path: '/personnes',
  component: Personnes,
})

const connexion = createRoute({
  getParentRoute: () => racine,
  path: '/connexion',
  validateSearch: z.object({ erreur: z.string().optional(), retour: z.string().optional() }),
  component: function PageConnexion() {
    const { erreur, retour } = connexion.useSearch()
    return <Connexion erreur={erreur} retour={retour} />
  },
})

export function creerRouteur(queryClient: QueryClient) {
  return createRouter({
    routeTree: racine.addChildren([
      connecte.addChildren([
        accueil,
        photo,
        envois,
        installer,
        recherche,
        taches,
        tache,
        projets,
        projet,
        echeancier,
        echeance,
        paiements,
        refacturations,
        abonnements,
        releves,
        pieces,
        piece,
        boiteEnvoi,
        brouillon,
        rapprochement,
        collecteurs,
        listeJustificatifs,
        justificatifs,
        sante,
        soin,
        circuits,
        accesTemporaires,
        entites,
        entite,
        planEntite,
        tiers,
        tiersFiche,
        personnes,
        reglages,
        autoriser,
        defi,
      ]),
      connexion,
    ]),
    context: { queryClient },
    defaultPreload: 'intent',
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof creerRouteur>
  }
}
